import type { Task, CaseDetails, TaskSummary, TaskCategory } from '../types/home';

/** Category display config — uses CSS custom properties for theme compatibility */
export const CATEGORY_CONFIG: Record<TaskCategory, { label: string; color: string; bg: string }> = {
  admin: { label: 'Admin', color: '#7c3aed', bg: 'var(--primary-50)' },
  client: { label: 'Client', color: '#2563eb', bg: 'var(--info-50)' },
  court: { label: 'Court', color: '#d97706', bg: 'var(--warning-50)' },
  defendant: { label: 'Defendant', color: '#dc2626', bg: 'var(--error-50)' },
  misc: { label: 'Misc', color: 'var(--text-tertiary)', bg: 'var(--surface-secondary)' },
};

export const CATEGORIES: TaskCategory[] = ['admin', 'client', 'court', 'defendant', 'misc'];

/** Check if a task status means "completed" */
export function isTaskCompleted(status: string | undefined): boolean {
  const s = (status ?? '').toLowerCase();
  return s === 'completed' || s === 'complete';
}

/** Check if a non-completed task is past its deadline */
export function isTaskOverdue(task: Task): boolean {
  if (isTaskCompleted(task.Status) || !task.Deadline) return false;
  return new Date(task.Deadline) < new Date();
}

/** Return a CSS-class-friendly status slug */
export function statusClass(status: string | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'completed' || s === 'complete') return 'completed';
  if (s === 'in progress') return 'inProgress';
  return 'default';
}

/** Extract the defendant name from a task or case details */
export function getDefendant(task: Task, caseDetailsMap: Record<string, CaseDetails>): string {
  const details = task.CaseRef ? caseDetailsMap[task.CaseRef] : undefined;
  if (details) {
    return details.defendant_name ?? details.Defendant ?? details.defendant ?? '';
  }
  return task.Defendant ?? task.defendant ?? '';
}

/** Extract the client full name from a task or case details */
export function getClientName(task: Task, caseDetailsMap: Record<string, CaseDetails>): string {
  const details = task.CaseRef ? caseDetailsMap[task.CaseRef] : undefined;
  const src = details ?? task;
  const parts = [src['First Name'], src['Middle Name'], src['Last Name']].filter(Boolean);
  return parts.join(' ');
}

/** Build "CaseRef - Client vs Defendant" summary */
export function caseSummary(task: Task, caseDetailsMap: Record<string, CaseDetails>): string {
  const client = getClientName(task, caseDetailsMap);
  const defendant = getDefendant(task, caseDetailsMap);
  return `${task.CaseRef ?? ''} - ${client} vs ${defendant}`;
}

/** Robustly extract case type from task/details */
export function getCaseType(task: Task, caseDetailsMap: Record<string, CaseDetails>): string {
  let caseType = task.CaseType ?? task.caseType ?? task.case_type ?? '';
  if (!caseType && task.CaseRef && caseDetailsMap[task.CaseRef]) {
    const d = caseDetailsMap[task.CaseRef];
    caseType = d.CaseType ?? d.caseType ?? d.case_type ?? '';
  }
  return caseType;
}

/** Compute task summary statistics */
export function computeSummary(tasks: Task[]): TaskSummary {
  const now = new Date();
  let inProgress = 0;
  let completed = 0;
  let overdue = 0;

  for (const t of tasks) {
    const s = (t.Status ?? '').toLowerCase();
    if (s === 'completed' || s === 'complete') {
      completed++;
    } else if (s === 'in progress') {
      inProgress++;
    }
    if (!isTaskCompleted(t.Status) && t.Deadline && new Date(t.Deadline) < now) {
      overdue++;
    }
  }

  return { total: tasks.length, inProgress, completed, overdue };
}

/** Sort tasks: non-completed by due date ASC, completed at bottom */
export function sortTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const aComp = isTaskCompleted(a.Status);
    const bComp = isTaskCompleted(b.Status);
    if (aComp && !bComp) return 1;
    if (!aComp && bComp) return -1;
    const aDue = a.Deadline ? new Date(a.Deadline).getTime() : Infinity;
    const bDue = b.Deadline ? new Date(b.Deadline).getTime() : Infinity;
    return aDue - bDue;
  });
}

/** Check if a task is due today */
export function isTaskDueToday(task: Task): boolean {
  if (!task.Deadline) return false;
  const d = new Date(task.Deadline);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** Auto-detect category from task description */
export function detectCategory(description: string): TaskCategory {
  const d = description.toLowerCase();
  // Defendant keywords
  if (
    /\bdef(ence|ense|endant)?\b/.test(d) ||
    /\baos\b/.test(d) ||
    /\blba\b/.test(d) ||
    /\btomlin\b/.test(d) ||
    d.includes('acknowledgement of service') ||
    d.includes('substantive response')
  ) {
    return 'defendant';
  }
  // Court keywords
  if (
    /\bdqs?\b/.test(d) ||
    /\bnopa\b/.test(d) ||
    /\bn180\b/.test(d) ||
    /\bnoi\b/.test(d) ||
    /form n/.test(d) ||
    /\bcourt\b/.test(d) ||
    /\bhearing\b/.test(d) ||
    /\bfiling\b/.test(d) ||
    /\ballocation\b/.test(d) ||
    /\bproceedings\b/.test(d)
  ) {
    return 'court';
  }
  // Client keywords
  if (/\bclient\b/.test(d) || /\bcl\b/.test(d) || /sign up/.test(d) || /\binstructions\b/.test(d)) {
    return 'client';
  }
  // Admin keywords
  if (
    /\bletter\b/.test(d) ||
    /\bdsar\b/.test(d) ||
    /close file/.test(d) ||
    /\bencrypted\b/.test(d) ||
    /\botp\b/.test(d) ||
    /\breview\b/.test(d)
  ) {
    return 'admin';
  }
  return 'misc';
}
